import { Component, signal, computed, input, ChangeDetectionStrategy } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { SeSearchService } from "./se.search.service";
import { PaginatedList } from "./paginated.list";
import { DEFAULT_PAGE_SIZE } from "./se.search.service";
import { convertEventDocumentToTileItem, convertOrganizationDocumentToTileItem } from "./tile.item.converters";
import { FormsModule } from "@angular/forms";
import { S25LoadingInlineStaticComponent } from "../../s25-loading/s25.loading.inline.static.component";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { S25IconButtonComponent } from "../../../standalone/s25.icon.button.component";
import { AriaLive } from "../../../services/aria.live.service";
import { PaginatedLoadAnnouncer } from "./paginated.load.announcer";

export type SESearchType = "organizations" | "events";
export interface SEOrgSearchConfig {
    compactResultLimit?: number;
    onViewAllResults?: () => void;
    defaultSearchType?: SESearchType; // Which type to show first (defaults to 'events')
}

@TypeManagerDecorator("s25-ng-se-search")
@Component({
    selector: "s25-ng-se-search",
    template: `
        <div class="search-type-toggle" role="tablist" aria-label="Search type">
            <button
                type="button"
                role="tab"
                class="type-tab"
                [class.active]="searchType() === 'events'"
                [attr.aria-selected]="searchType() === 'events'"
                (click)="setSearchType('events')"
            >
                <s25-ng-icon [type]="'calendarDate'" [ariaLabel]="false" />
                Events
            </button>
            <button
                type="button"
                role="tab"
                class="type-tab org-btn"
                [class.active]="searchType() === 'organizations'"
                [attr.aria-selected]="searchType() === 'organizations'"
                (click)="setSearchType('organizations')"
            >
                <s25-ng-icon [type]="'organization'" [ariaLabel]="false" />
                Organizations
            </button>
        </div>

        @if (errorMessage()) {
            <div class="error-message" role="alert">
                <s25-ng-icon [type]="'info'" [ariaLabel]="'Error'" />
                <pre class="ngBold">{{ errorMessage() }}</pre>
            </div>
        }

        <!-- Search bar -->
        <div class="search-wrapper">
            <span class="search-icon" aria-hidden="true">
                <s25-ng-icon [type]="'search'" [ariaLabel]="false" />
            </span>

            <input
                class="search-input"
                type="text"
                [placeholder]="placeholder()"
                [attr.aria-label]="'Search ' + searchType()"
                [(ngModel)]="searchInput"
                (input)="errorMessage.set(null)"
                (keyup.enter)="onSearch()"
            />

            @if (searchInput()) {
                <s25-ng-icon-button
                    class="clear-button"
                    [type]="'close'"
                    [size]="18"
                    [ariaLabel]="'Clear search'"
                    (clicked)="onReset()"
                />
            }

            <button
                type="button"
                class="search-button"
                [attr.aria-busy]="isSearching()"
                [attr.aria-disabled]="searchTermIsBlank()"
                (click)="onSearch()"
            >
                @if (isSearching()) {
                    <s25-ng-loading-inline-static class="search-button__spinner" [text]="null" aria-hidden="true" />
                }
                Search
            </button>
        </div>
    `,
    styles: `
        :host {
            color: var(--cn-fg-1);
            width: 100%;
            max-width: 640px;
            margin: 0 auto;
            display: flex;
            flex-direction: column;
        }

        .search-type-toggle {
            display: inline-flex;
            gap: 4px;
            padding: 4px;
            margin: 0 auto 18px;
            background: rgba(15, 23, 41, 0.35);
            border: 1px solid rgba(255, 255, 255, 0.22);
            border-radius: 999px;
            backdrop-filter: blur(12px);
            -webkit-backdrop-filter: blur(12px);
        }
        .type-tab {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 9px 20px;
            background: transparent;
            color: rgba(255, 255, 255, 0.8);
            border: 0;
            border-radius: 999px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition:
                color 180ms ease,
                background-color 180ms ease;
        }
        .type-tab:hover {
            color: #fff;
            background: rgba(255, 255, 255, 0.1);
        }
        .type-tab.active {
            background: #fff;
            color: var(--cn-navy);
            box-shadow:
                0 4px 12px -2px rgba(15, 23, 41, 0.25),
                0 1px 2px rgba(15, 23, 41, 0.1);
        }
        .type-tab.active:hover {
            background: #fff;
            color: var(--cn-navy);
        }
        .type-tab svg {
            vertical-align: middle;
        }

        :host .org-btn:not(.active) ::ng-deep svg {
            filter: brightness(0) invert(1);
        }

        .error-message {
            display: flex;
            align-items: center;
            gap: 10px;
            background: rgba(192, 57, 43, 0.18);
            border: 1px solid rgba(255, 180, 170, 0.45);
            border-radius: 10px;
            padding: 10px 14px;
            margin-bottom: 14px;
            color: #fff;
            text-align: left;
            backdrop-filter: blur(8px);
            -webkit-backdrop-filter: blur(8px);
        }
        .error-message pre {
            margin: 0;
            font-family: inherit;
            font-size: 13.5px;
            white-space: pre-wrap;
            word-wrap: break-word;
            color: #fff;
        }
        .ngBold {
            font-weight: 600;
        }

        .search-wrapper {
            position: relative;
            width: 100%;
            max-width: 640px;
            display: flex;
            align-items: center;
            background: #fff;
            border-radius: 999px;
            padding: 6px 6px 6px 22px;
            margin: 0 auto;
            box-shadow:
                0 2px 8px rgba(15, 23, 41, 0.08),
                0 18px 40px -12px rgba(15, 23, 41, 0.4);
            transition: box-shadow 220ms ease;
            box-sizing: border-box;
        }
        .search-wrapper:focus-within {
            box-shadow:
                0 0 0 4px rgba(255, 172, 28, 0.35),
                0 18px 44px -10px rgba(15, 23, 41, 0.45);
        }

        .search-icon {
            flex: 0 0 auto;
            color: var(--cn-fg-3);
            display: inline-flex;
            pointer-events: none;
        }

        .search-input {
            flex: 1;
            min-width: 0;
            padding: 14px 12px;
            border: 0;
            outline: 0;
            background: transparent;
            font-size: 16px;
            color: var(--cn-fg-1);
        }
        .search-input::placeholder {
            color: var(--cn-fg-4);
        }

        .clear-button {
            flex: 0 0 auto;
            margin-right: 4px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: transparent;
            border: 0;
            border-radius: 50%;
            cursor: pointer;
            transition:
                background-color 150ms ease,
                color 150ms ease;
        }

        :host .clear-button ::ng-deep svg {
            color: var(--cn-fg-3);
        }

        .clear-button:hover {
            background: #f1f3f7;
            color: var(--cn-fg-1);
        }

        .search-button {
            flex: 0 0 auto;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 12px 22px;
            background: var(--cn-navy);
            color: #fff;
            border: 0;
            border-radius: 999px;
            font-size: 14.5px;
            font-weight: 600;
            cursor: pointer;
            transition:
                background-color 150ms ease,
                transform 80ms ease;
        }
        .search-button:hover {
            background: var(--cn-navy-700);
        }
        .search-button__spinner {
            display: inline-flex;
        }
        :host .search-button__spinner ::ng-deep .c-loading {
            width: 16px;
        }
        :host .search-button__spinner ::ng-deep .c-loading__inner {
            stroke: currentColor;
        }
        .search-button:active {
            transform: translateY(1px);
        }

        *:focus-visible {
            outline: 2px solid var(--cn-orange);
            outline-offset: 3px;
            border-radius: 6px;
        }
        .search-wrapper:focus-within *:focus-visible {
            outline: none;
        }

        @media (max-width: 480px) {
            .search-type-toggle {
                gap: 2px;
                padding: 3px;
            }
            .type-tab {
                padding: 8px 14px;
                font-size: 13px;
                gap: 6px;
            }
            .search-wrapper {
                padding: 4px 4px 4px 14px;
                width: 95%;
            }
            .search-input {
                padding: 0;
                font-size: 15px;
            }
            .search-button {
                padding: 10px 16px;
                font-size: 13px;
            }
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, S25LoadingInlineStaticComponent, S25IconComponent, S25IconButtonComponent],
})
export class SeSearchComponent {
    // Configuration object - control all search features
    readonly config = input<SEOrgSearchConfig>({});

    // LocalStorage key for persisting search type
    private readonly SEARCH_TYPE_STORAGE_KEY = "se-search-type";

    // Search type state with localStorage persistence
    searchType = signal<SESearchType>(this.getInitialSearchType());

    // Search state
    searchInput = signal("");
    hasSearched = signal<boolean>(false);
    errorMessage = signal<string | null>(null);
    isExporting = signal<boolean>(false);

    // Paginated lists for each search type
    private eventsList = new PaginatedList(convertEventDocumentToTileItem, (items) => items);
    private organizationsList = new PaginatedList(convertOrganizationDocumentToTileItem, (items) => items);

    private readonly currentList = computed(() =>
        this.searchType() === "events" ? this.eventsList : this.organizationsList,
    );
    private readonly initialSearchInFlight = signal(false);

    // Computed signals that switch between lists based on search type
    results = computed(() =>
        this.searchType() === "events" ? this.eventsList.itemsForDisplay() : this.organizationsList.itemsForDisplay(),
    );
    resultsCount = computed(() =>
        this.searchType() === "events" ? this.eventsList.totalCount() : this.organizationsList.totalCount(),
    );
    isSearching = computed(() => this.initialSearchInFlight() || this.currentList().loading());
    currentPage = computed(() =>
        this.searchType() === "events" ? this.eventsList.page() : this.organizationsList.page(),
    );
    pageSize = signal<number>(DEFAULT_PAGE_SIZE);

    readonly searchTermIsBlank = computed(() => !this.searchInput().trim());

    readonly placeholder = computed(() =>
        this.searchType() === "events" ? "Search events..." : "Search organizations...",
    );

    /**
     * Get initial search type from localStorage or config
     */
    private getInitialSearchType(): SESearchType {
        // Check localStorage first
        const stored = localStorage.getItem(this.SEARCH_TYPE_STORAGE_KEY) as SESearchType;
        if (stored && (stored === "organizations" || stored === "events")) {
            return stored;
        }
        // Fall back to config or default to 'events'
        return this.config().defaultSearchType || "events";
    }

    /**
     * Set search type and persist to localStorage
     */
    setSearchType(type: SESearchType): void {
        this.searchType.set(type);
        localStorage.setItem(this.SEARCH_TYPE_STORAGE_KEY, type);

        // Reset search when switching types
        this.onReset();
    }

    /**
     * Handle text input changes
     */
    onTextInputChange(): void {
        this.errorMessage.set(null);
    }

    /**
     * Execute the search
     */
    async onSearch(): Promise<void> {
        if (this.searchTermIsBlank() || this.initialSearchInFlight()) return;

        const searchType = this.searchType();
        const searchTerm = this.searchInput().trim();
        const currentList = this.currentList();

        this.errorMessage.set(null);
        this.hasSearched.set(true);
        // Clear before the fetch so a re-search shows "Searching..." instead of the previous results
        currentList.reset();
        this.initialSearchInFlight.set(true);
        void AriaLive.announce(`Searching ${searchType}`);

        try {
            await this.fetchInitialResults(searchType, searchTerm);
            if (this.isStaleSearch(searchType, searchTerm)) return;

            const count = currentList.totalCount();
            void AriaLive.announce(
                count
                    ? `${count} ${PaginatedLoadAnnouncer.searchResultNoun(searchType, count)} found`
                    : "No results found",
            );
        } catch (error) {
            if (this.isStaleSearch(searchType, searchTerm)) return;

            const message = error.message || "An error occurred during search";
            this.errorMessage.set(message);
            this.hasSearched.set(false);
            void AriaLive.announce(message, true);
        } finally {
            this.initialSearchInFlight.set(false);
        }
    }

    private async fetchInitialResults(searchType: SESearchType, searchTerm: string): Promise<void> {
        if (searchType === "events") {
            this.eventsList.configureFetch(
                (page, pageSize, key) => SeSearchService.searchEventsByName(searchTerm, page, pageSize, key),
                this.pageSize(),
            );
            const response = await SeSearchService.searchEventsByName(searchTerm, 1, this.pageSize());
            if (this.isStaleSearch(searchType, searchTerm)) return;
            this.eventsList.setInitialData(response);
            return;
        }

        this.organizationsList.configureFetch(
            (page, pageSize, key) => SeSearchService.searchOrganizations(searchTerm, page, pageSize, key),
            this.pageSize(),
        );
        const response = await SeSearchService.searchOrganizations(searchTerm, 1, this.pageSize());
        if (this.isStaleSearch(searchType, searchTerm)) return;
        this.organizationsList.setInitialData(response);
    }

    /** A response is stale once the user has changed search type or edited the term mid-flight. */
    private isStaleSearch(searchType: SESearchType, searchTerm: string): boolean {
        return this.searchType() !== searchType || this.searchInput().trim() !== searchTerm;
    }

    /**
     * Handle "Load More" button click
     * Loads next page and appends to existing results
     * Preserves scroll position to avoid jumping to top
     */
    async onLoadMore(): Promise<void> {
        // Store current scroll position before loading
        const scrollPosition = window.scrollY || window.pageYOffset;

        try {
            await PaginatedLoadAnnouncer.loadWithAnnouncements(
                this.currentList(),
                "more",
                this.searchType(),
                this.pageSize(),
            );

            // Restore scroll position after a brief delay to allow DOM update
            setTimeout(() => {
                window.scrollTo({
                    top: scrollPosition,
                    behavior: "instant",
                });
            }, 0);
        } catch (error) {
            console.error("Error loading more:", error);
            this.errorMessage.set(error.message || "An error occurred while loading more results");

            // Restore scroll position even on error
            setTimeout(() => {
                window.scrollTo({
                    top: scrollPosition,
                    behavior: "instant",
                });
            }, 0);
        }
    }

    /**
     * Reset search to initial state
     */
    onReset(): void {
        this.searchInput.set("");
        this.hasSearched.set(false);
        this.errorMessage.set(null);
        this.eventsList.reset();
        this.organizationsList.reset();
    }
}
